// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.⁠​‌‌‌​​​​​‌‌‌​​‌​​‌‌​‌​​‌​‌‌​‌‌​‌​‌‌​‌‌‌‌​‌‌‌​​‌​​‌‌​​‌​​​‌‌​‌​​‌​‌‌​​​​‌⁠
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 2dd2d19e-1b76-45ae-a20f-b359114b8796

import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Chip, IconButton,
  Accordion, AccordionSummary, AccordionDetails, TextField, MenuItem, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Alert, AlertTitle,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FIELD_TYPES, resolvePicklist, relationMeta, TAG_GROUPS } from './schema';
import { TypeBadge, TagChips, CompletionBar, QualitySeal, LifecycleBar, fieldDisplay } from './ui';
import { printElementToPdf } from './exports';
import HistoryTab from './HistoryTab';
import RelationEditor from './RelationEditor';
import RelationsExplorer from './RelationsExplorer';
import IntegrationFlow from './IntegrationFlow';
import store from './store';

// One editable field control.
function FieldEditor({ field, value, onChange }) {
  const common = { size: 'small', fullWidth: true, value: value ?? '', onChange: (e) => onChange(e.target.value) };
  switch (field.type) {
    case FIELD_TYPES.TEXTAREA:
      return <TextField {...common} multiline minRows={2} />;
    case FIELD_TYPES.SELECT:
    case FIELD_TYPES.RATING:
    case FIELD_TYPES.ARROWS:
      return (
        <TextField {...common} select>
          <MenuItem value=""><em>—</em></MenuItem>
          {resolvePicklist(field.picklist).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    case FIELD_TYPES.BOOLEAN:
      return <Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} />;
    case FIELD_TYPES.NUMBER:
    case FIELD_TYPES.CURRENCY:
    case FIELD_TYPES.PERCENT:
      return <TextField {...common} type="number" onChange={(e) => onChange(Number(e.target.value))} />;
    default:
      return <TextField {...common} />;
  }
}

function Row({ label, children }) {
  return (
    <Box sx={{ display: 'flex', py: 0.75, borderBottom: '1px solid #f3f3f3' }}>
      <Box sx={{ width: 260, color: '#666', fontSize: 13, flexShrink: 0 }}>{label}</Box>
      <Box sx={{ flex: 1, fontSize: 14 }}>{children}</Box>
    </Box>
  );
}

export default function FactSheetDetail({ id, tenantId, onBack, onOpen }) {
  const [fs, setFs] = useState(null);
  const [schema, setSchema] = useState(null);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});
  const [tags, setTags] = useState([]);
  const [quality, setQuality] = useState('');
  const [integrations, setIntegrations] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [printAll, setPrintAll] = useState(false);
  const [newField, setNewField] = useState({ label: '', type: FIELD_TYPES.TEXT, section: 'custom' });

  const reload = () => {
    setNotFound(false);
    store.getFactSheet(id).then((data) => {
      // A deleted or unknown id resolves null. `schema` was then never set, so
      // the guard below stayed true forever: a bare "Loading…" box with no back
      // button and no way out except the browser's back arrow.
      if (!data) { setNotFound(true); return; }
      setFs(data);
      setDraft((data && data.fields) || {});
      setTags((data && data.tags) || []);
      setQuality((data && data.quality) || 'Draft');
      setIntegrations((data && data.integrations) || []);
      store.schemaFor(data.tenantId, data.type).then(setSchema);
    }).catch(() => setNotFound(true));
  };
  useEffect(reload, [id]);

  if (notFound) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>This fact sheet is no longer available</AlertTitle>
          It was probably deleted, or the link points at a record in a workspace you are not in.
        </Alert>
        {onBack && <Button variant="outlined" onClick={onBack}>Back to the inventory</Button>}
      </Box>
    );
  }
  if (!fs || !schema) return <Box sx={{ p: 4 }}>Loading…</Box>;

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    await store.saveFactSheet({ ...fs, fields: draft, tags, quality, integrations });
    setEdit(false);
    reload();
  };
  const cancel = () => { setEdit(false); reload(); };
  const removeSheet = async () => {
    if (!window.confirm(`Delete "${fs.name}"? This cannot be undone.`)) return;
    await store.deleteFactSheet(fs.id);
    onBack();
  };

  // --- tag editing ---
  const setTag = (i, patch) => setTags((t) => t.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addTag = () => setTags((t) => [...t, { group: 'Cell', value: '' }]);
  const delTag = (i) => setTags((t) => t.filter((_, j) => j !== i));

  // --- integration (data-flow) editing ---
  const setInt = (i, patch) => setIntegrations((a) => a.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addInt = () => setIntegrations((a) => [...a, { to: '', via: '', protocol: '', port: '', direction: 'outbound', dataType: '' }]);
  const delInt = (i) => setIntegrations((a) => a.filter((_, j) => j !== i));
  const addField = async () => {
    const key = 'cf_' + newField.label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    await store.addFieldDef(fs.tenantId, fs.type, { key, label: newField.label, type: newField.type });
    setAddOpen(false);
    setNewField({ label: '', type: FIELD_TYPES.TEXT });
    reload();
  };

  const renderValue = (field) =>
    edit ? (
      <FieldEditor field={field} value={draft[field.key]} onChange={(v) => setField(field.key, v)} />
    ) : (
      fieldDisplay(field, fs.fields?.[field.key])
    );

  const renderRelations = (relKeys = []) =>
    relKeys.map((rk) => {
      const meta = relationMeta(rk);
      const items = fs.resolvedRelations?.[rk] || [];
      return (
        <Row key={rk} label={meta?.label || rk}>
          {items.length ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {items.map((it) => (
                <Chip key={it.id} size="small" icon={<Box sx={{ ml: 0.5 }}><TypeBadge type={it.type} size={16} /></Box>}
                  label={it.name} onClick={() => onOpen(it.id)} sx={{ cursor: 'pointer' }} />
              ))}
            </Stack>
          ) : (
            <span style={{ color: '#aaa' }}>—</span>
          )}
        </Row>
      );
    });

  return (
    <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <TypeBadge type={fs.type} size={28} />
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>{fs.name}</Typography>
        <QualitySeal value={fs.quality} />
        {!edit ? (
          <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setEdit(true)}>Edit</Button>
        ) : (
          <>
            <Button onClick={cancel}>Cancel</Button>
            <Button startIcon={<SaveIcon />} variant="contained" color="success" onClick={save}>Save</Button>
          </>
        )}
        {/* A collapsed MUI Accordion carries INLINE height:0;overflow:hidden, and
            inline styles are exactly what survives the innerHTML copy in
            exports.js — so every section the user had not expanded printed as
            nothing, with no indication it was omitted. Expand everything for the
            duration of the export, then put it back.
            The button also sat outside the `edit` ternary: exporting mid-edit
            produced a page of blank boxes, because React sets input values as
            DOM properties, not attributes. */}
        <Tooltip title={edit ? 'Save or cancel before exporting — an in-progress edit prints as blank boxes' : 'Export to PDF'}>
          <span>
            <Button
              startIcon={<PictureAsPdfIcon />}
              variant="outlined"
              disabled={edit}
              onClick={() => {
                setPrintAll(true);
                // let the accordions paint open before the DOM is copied
                setTimeout(() => {
                  printElementToPdf('fs-print', fs.name);
                  setPrintAll(false);
                }, 60);
              }}
            >
              PDF
            </Button>
          </span>
        </Tooltip>
        {edit && (
          <Tooltip title="Delete this fact sheet">
            <Button color="error" variant="outlined" onClick={removeSheet}>Delete</Button>
          </Tooltip>
        )}
      </Stack>

      {/* Tags + quality seal are editable like everything else. */}
      {!edit ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <TagChips tags={fs.tags} />
          <Box sx={{ flex: 1 }} />
          <CompletionBar value={fs.completion} width={180} />
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'sectionHeader.main', fontWeight: 700 }}>Tags</Typography>
            <TextField select size="small" label="Quality seal" value={quality}
              onChange={(e) => setQuality(e.target.value)} sx={{ minWidth: 160 }}>
              {resolvePicklist('qualitySeal').map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<AddIcon />} onClick={addTag}>Add tag</Button>
          </Stack>
          {tags.map((t, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
              <TextField select size="small" label="Group" value={t.group}
                onChange={(e) => setTag(i, { group: e.target.value })} sx={{ minWidth: 150 }}>
                {TAG_GROUPS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Value" value={t.value} sx={{ flex: 1 }}
                onChange={(e) => setTag(i, { value: e.target.value })} />
              <IconButton size="small" onClick={() => delTag(i)}><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
          ))}
        </Paper>
      )}

      {/* Relations Explorer graph */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" sx={{ color: 'sectionHeader.main', fontWeight: 700 }}>Relations Explorer</Typography>
        <RelationsExplorer fs={fs} onOpen={onOpen} />
      </Paper>

      {/* Printable content */}
      <div id="fs-print">
        <h1>{fs.name}</h1>
        <Accordion defaultExpanded={(fs.integrations || []).length > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
              Integrations & Data Flow {fs.integrations?.length ? `(${fs.integrations.length})` : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {!edit ? (
              <IntegrationFlow appName={fs.name} integrations={fs.integrations || []} />
            ) : (
              <Box>
                {integrations.map((it, i) => (
                  <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center" flexWrap="wrap">
                    <TextField size="small" label="Connects to" value={it.to || ''} sx={{ minWidth: 170 }}
                      onChange={(e) => setInt(i, { to: e.target.value })} />
                    <TextField size="small" label="Via (engine)" value={it.via || ''} sx={{ minWidth: 170 }}
                      onChange={(e) => setInt(i, { via: e.target.value })} />
                    <TextField size="small" label="Protocol" value={it.protocol || ''} sx={{ minWidth: 130 }}
                      onChange={(e) => setInt(i, { protocol: e.target.value })} />
                    <TextField size="small" label="Port(s)" value={it.port || ''} sx={{ minWidth: 120 }}
                      onChange={(e) => setInt(i, { port: e.target.value })} />
                    <TextField select size="small" label="Direction" value={it.direction || 'outbound'} sx={{ minWidth: 140 }}
                      onChange={(e) => setInt(i, { direction: e.target.value })}>
                      {['outbound', 'inbound', 'bidirectional'].map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Data" value={it.dataType || ''} sx={{ minWidth: 140 }}
                      onChange={(e) => setInt(i, { dataType: e.target.value })} />
                    <IconButton size="small" onClick={() => delInt(i)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={addInt}>Add integration</Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        {schema.sections.map((section) => (
          <Accordion
            key={section.key}
            defaultExpanded={['information', 'classification', 'migrationKnowledge', 'portfolioStrategy'].includes(section.key)}
            {...(printAll ? { expanded: true } : {})}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>{section.label}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Lifecycle bar for the information section */}
              {section.key === 'information' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>Lifecycle</Typography>
                  <LifecycleBar from={fs.fields?.lifecycle} state={fs.fields?.lifecycleState} />
                </Box>
              )}
              {(section.fields || []).map((f) => <Row key={f.key} label={f.label + (f.mandatory ? ' *' : '')}>{renderValue(f)}</Row>)}
              {renderRelations(section.relations)}

              {/* Cloud Transformation subsections with per-team ownership + % complete */}
              {(section.subsections || []).map((sub) => (
                <Box key={sub.key} sx={{ mt: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{sub.label}</Typography>
                    <Chip size="small" variant="outlined" label={sub.team} sx={{ height: 18, fontSize: 10 }} />
                    <Box sx={{ flex: 1 }} />
                    {sub.complete && <CompletionBar value={fs.fields?.[sub.complete] || 0} width={140} />}
                  </Stack>
                  {(sub.fields || []).map((f) => <Row key={f.key} label={f.label}>{renderValue(f)}</Row>)}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>

      {/* Relationships, including the fields that live ON each connection */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>Relationships &amp; dependencies</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RelationEditor factSheet={fs} onSaved={reload} />
        </AccordionDetails>
      </Accordion>

      {/* Change history — who edited what, from the audit trail */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>Change history</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <HistoryTab factSheetId={fs.id} tenantId={fs.tenantId} />
        </AccordionDetails>
      </Accordion>

      {/* Add custom field — the flexible-schema superpower */}
      <Button startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => setAddOpen(true)}>
        Add custom field to {fs.type}
      </Button>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add custom field</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Field label" sx={{ mt: 1, mb: 2 }}
            value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} />
          <TextField select fullWidth size="small" label="Type"
            value={newField.type} onChange={(e) => setNewField({ ...newField, type: e.target.value })}>
            {[FIELD_TYPES.TEXT, FIELD_TYPES.TEXTAREA, FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY, FIELD_TYPES.BOOLEAN, FIELD_TYPES.DATE, FIELD_TYPES.URL].map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
            Applies to all {fs.type} fact sheets in this workspace. (Per-tenant schema — stored as a field definition.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newField.label} onClick={addField}>Add field</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
